import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { FacilityPromotionSummary } from "@bw/shared/types/generated";

export function PromotionSummaryCard({
  promotion,
  compact = false,
  dense = false,
}: {
  promotion: FacilityPromotionSummary;
  compact?: boolean;
  dense?: boolean;
}) {
  const eligibility =
    promotion.audience === "earned_referral"
      ? "Share a code; rewards unlock after verified fulfillment"
      : promotion.audience === "earned_loyalty"
        ? `Earn after ${promotion.reward_threshold ?? "the required"} verified services`
        : promotion.eligibility === "ineligible"
          ? "See offer terms"
          : "Special offer available";
  const denseEligibility =
    promotion.audience === "earned_referral"
      ? "Earn after your friend’s verified visit"
      : eligibility;
  const ordinaryServiceLabel = {
    court_reservation: "Court offer",
    open_play: "Drop-in offer",
    classes: "Class offer",
    coaching: "Coaching offer",
  }[promotion.service];
  const serviceLabel =
    promotion.audience === "earned_referral"
      ? "Referral reward"
      : promotion.audience === "earned_loyalty"
        ? `${ordinaryServiceLabel.replace(" offer", "")} loyalty reward`
        : ordinaryServiceLabel;
  return (
    <Box
      sx={{
        mt: compact ? 0.5 : 1,
        p: dense ? 1 : compact ? 1.25 : 1.75,
        border: 1,
        borderColor: "primary.main",
        borderRadius: 1.5,
        bgcolor: compact ? "background.paper" : "primary.main",
        color: compact ? "text.primary" : "primary.contrastText",
      }}
    >
      <Stack spacing={dense ? 0.4 : 0.75}>
        <Chip
          size="small"
          icon={<LocalOfferOutlinedIcon />}
          label={serviceLabel}
          sx={{
            alignSelf: "flex-start",
            bgcolor: "background.paper",
            ...(dense && {
              height: 22,
              "& .MuiChip-label": { px: 0.75, fontSize: "0.72rem" },
              "& .MuiChip-icon": { ml: 0.5, fontSize: "0.95rem" },
            }),
          }}
        />
        <Typography
          variant={compact ? "subtitle2" : "subtitle1"}
          sx={{
            fontWeight: 800,
            ...(dense && {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }),
          }}
        >
          {promotion.headline}
        </Typography>
        <Typography
          variant="body2"
          sx={
            dense
              ? {
                  display: "-webkit-box",
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  lineHeight: 1.35,
                }
              : undefined
          }
        >
          {promotion.summary}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            ...(dense && {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }),
          }}
        >
          {dense ? denseEligibility : eligibility}
        </Typography>
      </Stack>
    </Box>
  );
}
